import { create } from "zustand";
import { NotificationMessageDto } from "@/modules/types/notification";

interface NotificationGroup {
  notifications: NotificationMessageDto[];
  total: number;
  isLoading: boolean;
  limit: number;
  hasMore: boolean;
  nextId?: string;
}

interface NotificationState {
  notReadNoti: NotificationGroup;
  allNoti: NotificationGroup;
}

interface NotificationActions {
  setUnreadNoti: (
    payload: Partial<NotificationGroup> & {
      notifications: NotificationMessageDto[];
      isAppend?: boolean;
    }
  ) => void;
  setAllNoti: (
    payload: Partial<NotificationGroup> & {
      notifications: NotificationMessageDto[];
      isAppend?: boolean;
    }
  ) => void;
  setLoadingNotReadNoti: (isLoading: boolean) => void;
  setLoadingAllNoti: (isLoading: boolean) => void;
  setTotalNotReadNoti: (total: number) => void;
  setTotalAllNoti: (total: number) => void;
  setHasMoreNotReadNoti: (hasMore: boolean) => void;
  setHasMoreAllNoti: (hasMore: boolean) => void;
  setNextIdNotReadNoti: (nextId?: string) => void;
  setNextIdAllNoti: (nextId?: string) => void;
  appendNotReadNoti: (notifications: NotificationMessageDto[]) => void;
  appendAllNoti: (notifications: NotificationMessageDto[]) => void;
  setSeenNoti: (id: string) => void;
  removeNotReadNotiIndex: (id: string) => void;
  removeAllNotiIndex: (id: string) => void;
  reset: () => void;
}

export type NotificationStore = NotificationState & NotificationActions;

const initialGroupState: NotificationGroup = {
  notifications: [],
  total: 0,
  isLoading: false,
  limit: 24,
  hasMore: true,
  nextId: undefined,
};

const initialState: NotificationState = {
  notReadNoti: { ...initialGroupState },
  allNoti: { ...initialGroupState },
};

export const useNotificationStore = create<NotificationStore>((set) => ({
  ...initialState,

  setUnreadNoti: (payload) =>
    set((state) => ({
      notReadNoti: {
        ...state.notReadNoti,
        ...payload,
        notifications: payload.isAppend
          ? [
            ...state.notReadNoti.notifications,
            ...payload.notifications,
          ]
          : payload.notifications,
      },
    })),

  setAllNoti: (payload) =>
    set((state) => ({
      allNoti: {
        ...state.allNoti,
        ...payload,
        notifications: payload.isAppend
          ? [
            ...state.allNoti.notifications,
            ...payload.notifications,
          ]
          : payload.notifications,
      },
    })),

  setLoadingNotReadNoti: (isLoading) =>
    set((state) => ({
      notReadNoti: { ...state.notReadNoti, isLoading },
    })),

  setLoadingAllNoti: (isLoading) =>
    set((state) => ({
      allNoti: { ...state.allNoti, isLoading },
    })),

  setTotalNotReadNoti: (total) =>
    set((state) => ({
      notReadNoti: {
        ...state.notReadNoti,
        total,
        hasMore: total > state.notReadNoti.notifications.length,
      },
    })),

  setTotalAllNoti: (total) =>
    set((state) => ({
      allNoti: {
        ...state.allNoti,
        total,
        hasMore: total > state.allNoti.notifications.length,
      },
    })),

  setHasMoreNotReadNoti: (hasMore) =>
    set((state) => ({
      notReadNoti: { ...state.notReadNoti, hasMore },
    })),

  setHasMoreAllNoti: (hasMore) =>
    set((state) => ({
      allNoti: { ...state.allNoti, hasMore },
    })),

  setNextIdNotReadNoti: (nextId) =>
    set((state) => ({
      notReadNoti: { ...state.notReadNoti, nextId },
    })),

  setNextIdAllNoti: (nextId) =>
    set((state) => ({
      allNoti: { ...state.allNoti, nextId },
    })),

  appendNotReadNoti: (notifications) =>
    set((state) => ({
      notReadNoti: {
        ...state.notReadNoti,
        notifications: [...state.notReadNoti.notifications, ...notifications],
      },
    })),

  appendAllNoti: (notifications) =>
    set((state) => ({
      allNoti: {
        ...state.allNoti,
        notifications: [...state.allNoti.notifications, ...notifications],
      },
    })),

  setSeenNoti: (id) =>
    set((state) => ({
      notReadNoti: {
        ...state.notReadNoti,
        notifications: state.notReadNoti.notifications.map((n) =>
          n.id === id ? { ...n, isSeen: true } : n
        ),
      },
    })),

  removeNotReadNotiIndex: (id) =>
    set((state) => ({
      notReadNoti: {
        ...state.notReadNoti,
        notifications: state.notReadNoti.notifications.filter((n) => n.id !== id),
      },
    })),

  removeAllNotiIndex: (id) =>
    set((state) => ({
      allNoti: {
        ...state.allNoti,
        notifications: state.allNoti.notifications.filter((n) => n.id !== id),
      },
    })),
  
  reset: () => set(initialState),
}));
